import { useState, useMemo, useCallback } from 'react'
import { Photo, RouteData } from '@cya-routes/shared'

interface UsePhotoManagementProps {
  photos: Photo[]
  routes: RouteData[]
}

interface UsePhotoManagementReturn {
  selectedPhoto: Photo | null
  setSelectedPhoto: (photo: Photo | null) => void
  showPhotoModal: boolean
  setShowPhotoModal: (show: boolean) => void
  showPhotos: boolean
  setShowPhotos: (show: boolean) => void
  sortedPhotos: Photo[]
  handlePhotoSelect: (photo: Photo) => void
  handlePhotoNavigate: (direction: 'prev' | 'next') => void
}

/**
 * Custom hook that manages photo state and interactions
 * Handles photo selection, navigation, visibility, and sorting
 */
export function usePhotoManagement({
  photos,
  routes
}: UsePhotoManagementProps): UsePhotoManagementReturn {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null)
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  const [showPhotos, setShowPhotos] = useState(true)
  
  // Memoize sorted photos and fix incorrect routeIds
  const sortedPhotos = useMemo(() => {
    // First, fix any photos with 'master-route' as routeId
    const photosWithCorrectRouteId = photos.map(photo => {
      if (photo.routeId === 'master-route' && photo.distanceAlongRoute !== undefined) {
        // Find which segment this photo belongs to based on cumulative distance
        let cumulativeDistance = 0
        for (const route of routes) {
          const routeDistance = route.stats?.distance || 0
          if (photo.distanceAlongRoute >= cumulativeDistance && 
              photo.distanceAlongRoute < cumulativeDistance + routeDistance) {
            // Found the correct segment - adjust the distance to be relative to this segment
            return {
              ...photo,
              routeId: route.id,
              distanceAlongRoute: photo.distanceAlongRoute - cumulativeDistance
            }
          }
          cumulativeDistance += routeDistance
        }
      }
      return photo
    })
    
    // Now sort the photos
    return [...photosWithCorrectRouteId].sort((a, b) => {
      // First sort by route index
      const aRouteIndex = routes.findIndex(r => r.id === a.routeId)
      const bRouteIndex = routes.findIndex(r => r.id === b.routeId)
      
      if (aRouteIndex !== bRouteIndex) {
        return aRouteIndex - bRouteIndex
      }
      
      // Then sort by distance along the route
      const aDistance = a.distanceAlongRoute ?? Infinity
      const bDistance = b.distanceAlongRoute ?? Infinity
      
      return aDistance - bDistance
    })
  }, [photos, routes])
  
  // Handle photo selection
  const handlePhotoSelect = useCallback((photo: Photo) => {
    // Find the corrected photo from sortedPhotos
    const correctedPhoto = sortedPhotos.find(p => p.id === photo.id) || photo
    setSelectedPhoto(correctedPhoto)
    setShowPhotoModal(true)
  }, [sortedPhotos])
  
  // Handle photo navigation
  const handlePhotoNavigate = useCallback((direction: 'prev' | 'next') => {
    if (!selectedPhoto) return
    
    const currentIndex = sortedPhotos.findIndex(p => p.id === selectedPhoto.id)
    let newIndex: number
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : sortedPhotos.length - 1
    } else {
      newIndex = currentIndex < sortedPhotos.length - 1 ? currentIndex + 1 : 0
    }
    
    const newPhoto = sortedPhotos[newIndex]
    setSelectedPhoto(newPhoto)
  }, [selectedPhoto, sortedPhotos])
  
  return {
    selectedPhoto,
    setSelectedPhoto,
    showPhotoModal,
    setShowPhotoModal,
    showPhotos,
    setShowPhotos,
    sortedPhotos,
    handlePhotoSelect,
    handlePhotoNavigate
  }
}